import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonPopover,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { ReactElement, forwardRef, useCallback, useRef } from 'react';
import { useMeasure } from 'react-use';
import { PlanEntitlementKey } from '../../../../../../../api/graphql/generated';
import { PaywallWrapper } from '../../../../../../../components/paywall/paywall-wrapper';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import {
  MultipleWeldRecommendations,
  WeldSettingsRecommendation,
} from './settings-recommendation.hook';
import { WeldSettingsRecommendationCards } from './weld-settings-recommendation-cards';
import './weld-recommendations-popover.css';

interface WeldRecommendationsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: string; // ID of the trigger element
  recommendations?: MultipleWeldRecommendations;
  selectedIndex: number;
  equipment?: string;
  onSelectRecommendation: (
    index: number,
    recommendation: WeldSettingsRecommendation
  ) => void;
  isLoading: boolean;
}

export const WeldRecommendationsPopover = (
  props: WeldRecommendationsPopoverProps
): ReactElement => {
  const { isOpen, onClose, trigger } = props;

  const popoverRef = useRef<HTMLIonPopoverElement>(null);
  // We need to measure the height of the popover content to ensure the popover
  // is positioned correctly.
  const [popoverContentRef, { height }] = useMeasure<HTMLDivElement>();

  return (
    <IonPopover
      ref={popoverRef}
      trigger={trigger}
      isOpen={isOpen}
      onDidDismiss={onClose}
      showBackdrop={true}
      className="recommendations-popover"
      side="bottom"
      alignment="center"
      style={{
        '--height': `${height}px`,
      }}
    >
      <RecommendationsPopoverContent ref={popoverContentRef} {...props} />
    </IonPopover>
  );
};

const RecommendationsPopoverContent = forwardRef<
  HTMLDivElement,
  WeldRecommendationsPopoverProps
>((props: WeldRecommendationsPopoverProps, ref) => {
  const {
    onClose,
    recommendations,
    selectedIndex,
    equipment,
    isLoading,
    onSelectRecommendation,
  } = props;

  const handleRecommendationSelect = useCallback(
    (index: number, recommendation: WeldSettingsRecommendation) => {
      onSelectRecommendation(index, recommendation);
      onClose(); // Close popover after selection
    },
    [onSelectRecommendation, onClose]
  );

  return (
    <div ref={ref}>
      <IonHeader>
        <IonToolbar>
          <IonTitle size="small">
            {getLocalizedText('welding:ai_suggestions')}
          </IonTitle>
          <IonButton
            slot="end"
            fill="clear"
            onClick={onClose}
            className="popover-close-button"
          >
            <IonIcon icon={close} />
          </IonButton>
        </IonToolbar>
      </IonHeader>
      <IonContent className="recommendations-popover-content">
        <PaywallWrapper
          planEntitlementKey={PlanEntitlementKey.AI_RECOMMENDATIONS}
        >
          {recommendations && recommendations.recommendations.length > 0 ? (
            <div className="popover-recommendations">
              <WeldSettingsRecommendationCards
                recommendations={recommendations.recommendations}
                selectedIndex={selectedIndex}
                equipment={equipment}
                onSelectRecommendation={handleRecommendationSelect}
              />
            </div>
          ) : (
            <div className="no-recommendations-popover">
              <IonText color="medium">
                {isLoading
                  ? getLocalizedText('welding:loading_recommendations')
                  : getLocalizedText('welding:no_recommendations_available')}
              </IonText>
            </div>
          )}
        </PaywallWrapper>
      </IonContent>
    </div>
  );
});

RecommendationsPopoverContent.displayName = 'RecommendationsPopoverContent';
